// src/components/Hero.tsx
import React, { useState, useEffect, useRef } from "react";

type Props = {
  bgImageUrl?: string;
  onSchedule?: () => void;
};

type Metric = {
  id: string;
  highlight: string;
  label: string;
};

const metrics: Metric[] = [
  { id: "m1", highlight: "+50,000", label: "Servicios realizados" },
  { id: "m2", highlight: "24 h", label: "Pintura express" },
  { id: "m3", highlight: "Pioneros", label: "En el mercado" },
];

// Componente para animar números
const AnimatedNumber: React.FC<{ target: number; prefix?: string; suffix?: string }> = ({ 
  target, 
  prefix = "", 
  suffix = "" 
}) => {
  const [count, setCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.5 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible) return;

    const duration = 2000; // 2 segundos
    const steps = 60; // 60 pasos para animación suave
    const increment = target / steps;
    let current = 0;

    const timer = setInterval(() => {
      current += increment;
      if (current >= target) {
        setCount(target);
        clearInterval(timer);
      } else {
        setCount(Math.floor(current));
      }
    }, duration / steps);

    return () => clearInterval(timer);
  }, [isVisible, target]);

  return (
    <span ref={ref}>
      {prefix}{count.toLocaleString()}{suffix}
    </span>
  );
};

const Hero: React.FC<Props> = ({
  bgImageUrl = "/images/background_img.png",
  onSchedule,
}) => {
  const [isHovered, setIsHovered] = useState(false);

  const handleScheduleClick = () => {
    onSchedule?.();
    if (!onSchedule) console.log("Scheduling appointment…");
  };

  return (
    <section
      className="relative w-full min-h-screen overflow-hidden bg-black text-white"
      aria-label="Sección de llamada a la acción Magníficos"
    >
      {/* Fondo */}
      <div className="absolute inset-0">
        <img
          src={bgImageUrl}
          alt="Equipo Magníficos frente a la plaza"
          className="w-full h-full object-cover transform translate-y-50 md:-translate-y-50 object-center"
          style={{
            objectPosition: "center center" // centrado en mobile y desktop
          }}
        />

        {/* Degradado superior (para legibilidad de títulos) */}
        <div
          className="pointer-events-none absolute inset-x-0 top-0"
          style={{
            height: "55vh",
            background:
              "linear-gradient(180deg, rgba(0,0,0,1) 0%, rgba(0,0,0,0.85) 45%, rgba(0,0,0,0) 100%)",
            mixBlendMode: "multiply",
          }}
        />

        {/* Degradado inferior (banda oscura donde "se apoyan" las cards) */}
        <div
          className="pointer-events-none absolute inset-x-0 bottom-0"
          style={{
            height: "45vh",
            background:
              "linear-gradient(0deg, rgba(0,0,0,1) 0%, rgba(0,0,0,0.8) 55%, rgba(0,0,0,0) 100%)",
            mixBlendMode: "multiply",
          }}
        />
      </div>

      {/* Contenido */}
      <div className="relative z-10 mx-auto flex flex-col items-center px-4 py-10 sm:px-6 md:py-16">
        <div className="w-full max-w-6xl px-4 py-4 md:px-8 md:py-6">
          <h1 className="text-center font-extrabold leading-snug text-2xl sm:text-3xl md:text-4xl lg:text-5xl" style={{ fontSize: 'clamp(1.5rem, 4vw, 3rem)' }}>
            Invierte en la franquicia que ya está{" "}
            <span style={{ color: "#F7AC48" }}>funcionando</span>
          </h1>

          <p className="mx-auto mt-2 max-w-5xl text-center opacity-90 md:mt-3 text-sm sm:text-base md:text-lg lg:text-xl xl:text-2xl">
            Ya lo probamos en Bogotá. Sabemos lo que funciona. Tenemos la
            tracción, la validación del modelo y el sistema listo para escalar.
          </p>

          <p className="mx-auto mt-3 sm:mt-4 max-w-5xl text-center font-semibold text-base sm:text-lg md:text-xl lg:text-2xl xl:text-3xl">
            Ahora puedes ser parte de Los Magníficos y
            abrir tu propia sede en otra ciudad de Colombia o Latinoamérica
          </p>

          <div className="mt-4 sm:mt-6 flex justify-center">
            <button
              type="button"
              aria-label="Agendar una cita para abrir sede"
              onClick={handleScheduleClick}
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
              className={`rounded-[10px] px-6 sm:px-8 md:px-10 py-3 sm:py-[14px] md:py-[15px] text-center text-sm sm:text-base md:text-[18px] lg:text-[20px] font-semibold text-black transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-amber-300 ${
                isHovered ? "scale-[1.03] shadow-lg" : ""
              }`}
              style={{ backgroundColor: "#F59E0B" }}
            >
              Agendar una cita
            </button>
          </div>
        </div>

        <div className="w-full pt-16 sm:pt-24 md:pt-48 lg:pt-64 xl:pt-80" />
        <div className="w-full max-w-6xl px-4 sm:px-6 pb-10 md:pb-14 relative z-20 mt-80 sm:mt-96 md:mt-[450px] lg:mt-[500px] xl:mt-[800px]">
          {/* Mobile: carrusel */}
          <div className="md:hidden">
            <div
              className="flex snap-x snap-mandatory gap-4 overflow-x-auto pb-2 scrollbar-hide"
              aria-label="Indicadores de valor"
              style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
            >
              {metrics.map((m) => (
                <article
                  key={m.id}
                  className="min-w-[15.625rem] sm:min-w-[16.875rem] snap-center rounded-xl bg-black/60 p-5 sm:p-6 shadow-xl ring-1 ring-white/5 backdrop-blur-sm text-center hover:bg-black/75 hover:scale-105 hover:-translate-y-1 hover:shadow-2xl hover:ring-2 hover:ring-amber-400/50 transition-all duration-300 ease-out cursor-pointer group"
                  style={{ borderColor: '#FF9200', borderWidth: '1px', borderStyle: 'solid' }}
                >
                  {m.id === "m1" ? (
                    <div className="text-2xl sm:text-3xl font-extrabold group-hover:scale-110 group-hover:text-amber-400 transition-all duration-300" style={{ color: '#FF9200' }}>
                      <AnimatedNumber target={50000} prefix="+" />
                    </div>
                  ) : (
                    <div className="text-2xl sm:text-3xl font-extrabold group-hover:scale-110 group-hover:text-amber-400 transition-all duration-300" style={{ color: '#FF9200' }}>
                      {m.highlight}
                    </div>
                  )}
                  <div className="mt-2 text-xs sm:text-sm opacity-90 group-hover:opacity-100 group-hover:text-white transition-all duration-300">
                    {m.label}
                  </div>
                </article>
              ))}
            </div>
            <div className="flex justify-center gap-2 mt-3">
              {metrics.map((_, i) => (
                <div key={i} className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: '#FF9200', opacity: 0.5 }} />
              ))}
            </div>
          </div>

          {/* Desktop: grid */}
          <div className="hidden md:grid grid-cols-3 gap-6">
            {metrics.map((m) => (
              <article
                key={m.id}
                className="rounded-xl bg-black/65 p-6 lg:p-8 text-center shadow-2xl ring-1 ring-white/5 backdrop-blur-sm hover:bg-black/75 hover:scale-105 hover:-translate-y-2 hover:shadow-3xl hover:ring-2 hover:ring-amber-400/50 transition-all duration-300 ease-out cursor-pointer group"
                style={{ borderColor: '#FF9200', borderWidth: '1px', borderStyle: 'solid' }}
              >
                {m.id === "m1" ? (
                  <div className="text-3xl lg:text-4xl font-extrabold leading-none group-hover:scale-110 group-hover:text-amber-400 transition-all duration-300" style={{ color: '#FF9200' }}>
                    <AnimatedNumber target={50000} prefix="+" />
                  </div>
                ) : (
                  <div className="text-3xl lg:text-4xl font-extrabold leading-none group-hover:scale-110 group-hover:text-amber-400 transition-all duration-300" style={{ color: '#FF9200' }}>
                    {m.highlight}
                  </div>
                )}
                <div className="mt-3 text-sm lg:text-base opacity-90 group-hover:opacity-100 group-hover:text-white transition-all duration-300">
                  {m.label}
                </div>
              </article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;